import { useState, createContext, useEffect } from 'react'
import { toast } from 'react-toastify'

import { addressService } from '@/services/utils/addressService'
import { utilityService } from '@/services/Parametrization/utilityService'
import { companyService } from '@/services/Parametrization/companyService'
import { categoryService } from '@/services/Parametrization/categoryService'
import { vehicleService } from '@/services/utils/vehicleService'
import { CONSTANTS } from '@/utils/CONSTANTS'
import { usersService } from '@/services/Users'
import { utilityPreparationService } from '@/services/Preparation/utilityPreparationService'
import { vehicleServiceV2 } from '@/services/utils/v2/vehicleService'

interface IGlobalInformations {
  children: React.ReactNode
}

type GlobalInformationsContextData = {
  INTERPRISE_OPTIONS: any
  INTERPRISE_LIST: any
  STATE_LIST: any
  STATE_OPTIONS: any
  SOCIAL_OPTIONS: any
  BRAND_LIST: any
  BRAND_OPTIONS: any
  CARMAKER_LIST: any
  CARMAKER_OPTIONS: any
  MODEL_LIST: any
  MODEL_OPTIONS: any
  CATEGORY_LIST: any
  CATEGORY_OPTIONS: any
  CR_LIST: any
  CR_OPTIONS: any
  DIRECTORSHIP_LIST: any
  DIRECTORSHIP_OPTIONS: any
  COLORS_LIST: any
  REQUESTER_LIST: any
  OWNER_LIST: any
  PROFILE_LIST: any[]
  PROFILE_OPTIONS: any[]
  GET_SELECTS: (params?: any) => any
  GET_COLORS: (params?: any) => {}
  GET_CR: (params?: any) => Promise<any>
  GET_MODEL: (params?: any) => Promise<void>
  GET_INTERPRISE: (params?: any) => Promise<void>
  GET_STATES: (params?: any) => Promise<void>
  GET_OWNER: (params?: any) => Promise<void>
  GET_BOARD: (params?: any) => {}
  GET_CARMAKER: (params?: any) => {}
  GET_DIRECTORSHIP: (params?: any) => Promise<void>
  GET_VEHICLE: (params?: any) => Promise<void>
  SET_MODEL_OPTIONS: any
}

export const GlobalInformations = createContext(
  {} as GlobalInformationsContextData
)

export function GlobalInformationsProvider({ children }: IGlobalInformations) {
  const [STATE_LIST, SET_STATE_LIST] = useState([])
  const [MODEL_LIST, SET_MODEL_LIST] = useState([])
  const [MODEL_OPTIONS, SET_MODEL_OPTIONS] = useState([])
  const [CARMAKER_LIST, SET_CARMAKER_LIST] = useState([])
  const [CARMAKER_OPTIONS, SET_CARMAKER_OPTIONS] = useState([])
  const [CATEGORY_LIST, SET_CATEGORY_LIST] = useState<any>({})
  const [CATEGORY_OPTIONS, SET_CATEGORY_OPTIONS] = useState([])
  const [CR_LIST, SET_CR_LIST] = useState([])
  const [CR_OPTIONS, SET_CR_OPTIONS] = useState([])
  const [INTERPRISE_LIST, SET_INTERPRISE_LIST] = useState<any>({})
  const [INTERPRISE_OPTIONS, SET_INTERPRISE_OPTIONS] = useState([])
  const [STATE_OPTIONS, SET_STATE_OPTIONS] = useState([])
  const [BRAND_LIST, SET_BRAND_LIST] = useState<any>({})
  const [BRAND_OPTIONS, SET_BRAND_OPTIONS] = useState([])
  const [SOCIAL_OPTIONS, SET_SOCIAL_OPTIONS] = useState([])
  const [DIRECTORSHIP_LIST, SET_DIRECTORSHIP_LIST] = useState([])
  const [DIRECTORSHIP_OPTIONS, SET_DIRECTORSHIP_OPTIONS] = useState([])
  const [COLORS_LIST, SET_COLORS_LIST] = useState([])
  const [COLORS_OPTIONS, SET_COLORS_OPTIONS] = useState([])
  const [REQUESTER_LIST, SET_REQUESTER_LIST] = useState([])
  const [OWNER_LIST, SET_OWNER_LIST] = useState([])
  const [PROFILE_LIST, SET_PROFILE_LIST] = useState([])
  const [PROFILE_OPTIONS, SET_PROFILE_OPTIONS] = useState([])

  useEffect(() => {
    SET_DATA_SELECTS()
  }, [
    INTERPRISE_LIST,
    STATE_LIST,
    BRAND_LIST,
    CR_LIST,
    DIRECTORSHIP_LIST,
    COLORS_LIST,
    MODEL_LIST,
    PROFILE_LIST
  ])

  async function GET_SELECTS() {
    try {
      const tasks = [
        {
          condition: !STATE_LIST.length,
          promise: () => addressService.getStates(),
          setter: SET_STATE_LIST
        },
        {
          condition: !BRAND_LIST?.rows?.length,
          promise: () => utilityService.getBrand(),
          setter: SET_BRAND_LIST
        },
        {
          condition: !CARMAKER_LIST.length,
          promise: () => utilityService.getCarmaker(),
          setter: SET_CARMAKER_LIST
        },
        {
          condition: !MODEL_LIST.length,
          promise: () => utilityService.getModel({ ativo: true }),
          setter: SET_MODEL_LIST
        },
        {
          condition: !CATEGORY_LIST?.rows?.length,
          promise: () => categoryService.get({}),
          setter: SET_CATEGORY_LIST
        },
        {
          condition: !INTERPRISE_LIST?.rows?.length,
          promise: () =>
            companyService.get({
              ativo: true,
              pageSize: CONSTANTS.allRegisters
            }),
          setter: SET_INTERPRISE_LIST
        },
        {
          condition: !CR_LIST.length,
          promise: () => utilityService.getCR(),
          setter: (data: any) => {
            const crData = data.map((x: any) => ({
              ...x,
              descricao: `${x.numeroCR} - ${x.descricao}`
            }))
            SET_CR_LIST(crData)
          }
        },
        {
          condition: !DIRECTORSHIP_LIST.length,
          promise: () => utilityService.getDirectorship(),
          setter: SET_DIRECTORSHIP_LIST
        },
        {
          condition: !COLORS_LIST.length,
          promise: () => vehicleService.getVehicleColor(),
          setter: SET_COLORS_LIST
        },
        {
          condition: !REQUESTER_LIST.length,
          promise: () =>
            usersService.getRequestingUsers({
              pageSize: CONSTANTS.allRegisters
            }),
          setter: (data: any) => SET_REQUESTER_LIST(data.rows)
        },
        {
          condition: !OWNER_LIST.length,
          promise: () =>
            utilityPreparationService.getOwner({
              pageSize: CONSTANTS.allRegisters
            }),
          setter: SET_OWNER_LIST
        },
        {
          condition: !PROFILE_LIST.length,
          promise: () => usersService.getPerfis(),
          setter: SET_PROFILE_LIST
        }
      ]

      const promises = tasks
        .filter((task) => task.condition)
        .map((task) => task.promise())
      const results = await Promise.all(promises)

      let resultIndex = 0
      tasks.forEach((task, index) => {
        if (task.condition) {
          const result: any = results[resultIndex++]
          if (result?.data?.data) {
            task.setter(result.data.data)
          }
        }
      })

      return results
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PROFILES() {
    try {
      const res: any = await usersService.getPerfis()
      SET_PROFILE_LIST(res.data.data)
    } catch (e: any) {
      toast.error(e?.message)
    }
  }

  async function GET_COLORS() {
    try {
      const res: any = await utilityService.getColors()
      SET_COLORS_LIST(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_MODEL(params?: any) {
    try {
      const res: any = await utilityService.getModel(params)
      const models = await res.data.data

      if (!models?.length) {
        SET_MODEL_LIST([])
        return toast.warning('Não há modelos para esta marca')
      }

      SET_MODEL_LIST(models)
      return models
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_CR(params: any) {
    try {
      const res: any = await utilityService.getCR(params)
      const dataTemp = res.data.data

      if (dataTemp)
        dataTemp.forEach(
          (x: any) => (x.descricao = `${x.codigo} - ${x.descricao}`)
        )

      SET_CR_LIST(dataTemp)
      return dataTemp
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_INTERPRISE(params?: any) {
    try {
      const res: any = await companyService.get({
        ativo: true,
        pageSize: CONSTANTS.allRegisters,
        ...params
      })
      const enterprises = res.data.data
      SET_INTERPRISE_LIST(enterprises)
      return enterprises
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_OWNER(params?: any) {
    try {
      const res: any = await utilityPreparationService.getOwner({
        pageSize: CONSTANTS.allRegisters
      })
      const owners = res.data.data
      SET_OWNER_LIST(owners)
      return owners
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_STATES(params?: any) {
    try {
      const res: any = await addressService.getStates()
      const states = res.data.data
      SET_STATE_LIST(states)
      return states
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_BOARD(params?: any) {
    try {
      const res: any = await utilityPreparationService.getBoard()
      SET_DIRECTORSHIP_LIST(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_CARMAKER(params?: any) {
    try {
      const res: any = await utilityService.getCarmaker()
      SET_CARMAKER_LIST(res.data.data)
      return res.data.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_DIRECTORSHIP() {
    try {
      const res: any = await utilityService.getDirectorship()
      const directorshipData = res.data.data
      SET_DIRECTORSHIP_LIST(directorshipData)
      return directorshipData
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_VEHICLE(params?: any) {
    try {
      const res: any = await vehicleServiceV2.getVeiculo(params)
      return res?.data?.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function SET_DATA_SELECTS() {
    let arrOfInterprise: any = []
    let arrOfStates: any = []
    let arrOfBrands: any = []
    let arrOfCarmaker: any = []
    let arrOfModels: any = []
    let arrOfCategories: any = []
    let arrOfCr: any = []
    let arrOfSocial: any = []
    let arrOfDirectorship: any = []
    let arrOfColors: any = []
    let arrUserProfile: any = []

    INTERPRISE_LIST?.rows?.forEach((item: any) => {
      arrOfInterprise.push(item?.nomeFantasia)
    })

    INTERPRISE_LIST?.rows?.forEach((item: any) => {
      arrOfSocial.push(item?.razaoSocial)
    })

    STATE_LIST.forEach((item: any) => {
      arrOfStates.push(item?.nome)
    })

    BRAND_LIST?.rows?.forEach((item: any) => {
      arrOfBrands.push(item?.nome)
    })

    CARMAKER_LIST.forEach((item: any) => {
      arrOfCarmaker.push(item?.montadora)
    })

    MODEL_LIST.forEach((item: any) => {
      arrOfModels.push({
        descricao: item?.nome,
        descricaoFipe: `${item?.nome} - ${item?.codigoFipe}`,
        id: item.id,
        montadoraId: item.montadoraId,
        categoria: item.categoria
      })
    })

    CATEGORY_LIST?.rows?.forEach((item: any) => {
      arrOfCategories.push(item?.nome)
    })

    CR_LIST.forEach((item: any) => {
      arrOfCr.push(item?.descricao)
    })

    DIRECTORSHIP_LIST.forEach((item: any) => {
      arrOfDirectorship.push(item?.descricao)
    })

    COLORS_LIST.forEach((item: any) => {
      arrOfColors.push(item?.nome)
    })

    PROFILE_LIST.forEach((item: any) => {
      arrUserProfile.push(item?.nome)
    })

    await SET_INTERPRISE_OPTIONS(arrOfInterprise)
    await SET_STATE_OPTIONS(arrOfStates)
    await SET_BRAND_OPTIONS(arrOfBrands)
    await SET_CARMAKER_OPTIONS(arrOfCarmaker)
    await SET_MODEL_OPTIONS(arrOfModels)
    await SET_CATEGORY_OPTIONS(arrOfCategories)
    await SET_CR_OPTIONS(arrOfCr)
    await SET_SOCIAL_OPTIONS(arrOfSocial)
    await SET_DIRECTORSHIP_OPTIONS(arrOfDirectorship)
    await SET_COLORS_OPTIONS(arrOfColors)
    await SET_PROFILE_OPTIONS(arrUserProfile)
  }

  return (
    <GlobalInformations.Provider
      value={{
        INTERPRISE_LIST,
        INTERPRISE_OPTIONS,
        STATE_LIST,
        SET_MODEL_OPTIONS,
        STATE_OPTIONS,
        BRAND_LIST,
        BRAND_OPTIONS,
        CARMAKER_LIST,
        CARMAKER_OPTIONS,
        MODEL_LIST,
        MODEL_OPTIONS,
        CATEGORY_LIST,
        CATEGORY_OPTIONS,
        SOCIAL_OPTIONS,
        CR_LIST,
        CR_OPTIONS,
        DIRECTORSHIP_LIST,
        DIRECTORSHIP_OPTIONS,
        COLORS_LIST,
        REQUESTER_LIST,
        OWNER_LIST,
        PROFILE_LIST,
        PROFILE_OPTIONS,
        GET_SELECTS,
        GET_COLORS,
        GET_CR,
        GET_MODEL,
        GET_INTERPRISE,
        GET_STATES,
        GET_OWNER,
        GET_BOARD,
        GET_CARMAKER,
        GET_DIRECTORSHIP,
        GET_VEHICLE
      }}
    >
      {children}
    </GlobalInformations.Provider>
  )
}
